import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppSettings } from '../../config/config';
import { BrandService } from '../brand/brand.service';

// Parent-app's own mobile-OTP login endpoints (see LoginScreen.js /
// constant.js / api.js in the parent mobile app repo) — reused as-is here
// so the web login stays interchangeable with the mobile app's session
// data. otpHash has no web equivalent (it's for Android's SMS Retriever
// auto-read), but the backend expects a non-empty value, so the web client
// sends the same fixed placeholder hash the app's iOS build uses;
// mobileClientType/mobileAppTech identify this as the web client rather
// than a real ios/android build.
const OTP_HASH = '#23yej';

@Injectable({ providedIn: 'root' })
export class ParentLoginService {

  // These are the parent mobile app's own endpoints (api.js), which live on
  // the mobile server host, not the web admin's API_ENDPOINT_V3 host. The
  // "for/web" suffix is a web-specific variant of each endpoint (distinct
  // from the app's own, which lack it) — confirmed against the backend
  // team's reference curls for generate/verify OTP (mobile + email) and
  // logout.
  private base = AppSettings.API_ENDPOINT_STAFF;

  constructor(private http: HttpClient, private brandService: BrandService) { }

  sendOtpForMobile(countryCode: string, mobileNumber: string) {
    return this.http.post(`${this.base}/generate/otp/for/parent/login/with/mobile/number/for/web`, {
      countryCode,
      mobileNumber,
      otpHash: OTP_HASH,
      partnerDomain: this.brandService.brand.domainSuffix,
    });
  }

  sendOtpForEmail(emailId: string) {
    return this.http.post(`${this.base}/generate/otp/for/parent/login/with/email/for/web`, {
      emailId,
      otpHash: OTP_HASH,
      partnerDomain: this.brandService.brand.domainSuffix,
    });
  }

  verifyOtpForMobile(otp: string, countryCode: string, mobileNumber: string) {
    return this.http.post(`${this.base}/verify/otp/for/parent/login/with/mobile/number/for/web`, {
      countryCode,
      mobileNumber,
      otp,
    });
  }

  verifyOtpForEmail(otp: string, emailId: string) {
    return this.http.post(`${this.base}/verify/otp/for/parent/login/with/email/for/web`, {
      emailId,
      otp,
    });
  }

  // Backend session invalidation on logout — previously this app only ever
  // cleared localStorage client-side, leaving the session token valid on
  // the server. authToken header carries the session token issued at OTP
  // verify (see ParentLoginComponent.onLoginSuccess's localStorage write);
  // the staff-api.interceptor only adds its own usertoken cookie when this
  // header is absent, so this call is correctly treated as a parent request.
  logout() {
    const headers = new HttpHeaders().append('authToken', this.getAuthToken() || '');
    return this.http.put(`${this.base}/log/out/student/web`, {}, { headers });
  }

  // Old-style endpoint (BWApiConnector.js's getKidsFromSchool), separate
  // from the versionThree API used above — unversioned, lives directly
  // under the mobile server host (no /api/v3 prefix), hence
  // API_ENDPOINT_STAFF_BASE rather than this.base. Returns the full kid
  // list including studentImage, which the OTP-verify response's
  // schoolAndStudentDetailData never carries.
  getAllKidInfoForSecondaryParent(mobileNumber: string) {
    const headers = new HttpHeaders().append('authToken', this.getAuthToken() || '');
    return this.http.post(`${AppSettings.API_ENDPOINT_STAFF_BASE}/getAllKidInfoForSecondaryParent`,
      { mobileNumber }, { headers });
  }

  private getAuthToken(): string | null {
    const raw = localStorage.getItem('authToken');
    if (!raw) { return null; }
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
}
